import React, { useState, useEffect } from 'react';
import secureStorage from '../../lib/secureStorage';
import authedFetch from '../../lib/authedFetch';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, ArrowRight, Loader2, TrendingDown, Sparkles } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function LeiDivergenceDigestCard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    const token = secureStorage.getItem('session_token');
    authedFetch(`${API_URL}/api/lei-health/divergence-digest`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <Card data-testid="lei-divergence-digest-loading"><CardContent className="py-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></CardContent></Card>;
  }
  const alerts = data?.alerts || [];

  return (
    <Card className="border-amber-200 bg-gradient-to-br from-amber-50/70 to-white mb-6" data-testid="lei-divergence-digest">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Sparkles className="h-5 w-5 text-amber-600" /> LEI Influence–Reality Digest
            {data?.new_count > 0 && <Badge className="bg-amber-600 text-white" data-testid="digest-new-badge">{data.new_count} new</Badge>}
          </CardTitle>
          <button onClick={() => navigate('/lei-health/score-trends')} data-testid="digest-open-trends"
            className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 hover:text-amber-800">
            Open Score Trends <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </div>
        <p className="text-xs text-slate-500">
          KPIs where learning is designed to drive strongly but the observed correlation is weak or sliding. Updated weekly (also emailed to admins).
        </p>
      </CardHeader>
      <CardContent>
        {alerts.length === 0 ? (
          <p className="text-sm text-slate-500 py-2" data-testid="digest-empty">No influence–reality gaps right now — designed influence and observed correlation are aligned. 🎉</p>
        ) : (
          <ul className="space-y-2">
            {alerts.map((a) => (
              <li key={a.kpi} data-testid={`digest-alert-${a.kpi}`}
                className="flex items-center gap-3 rounded-lg border border-amber-100 bg-white/70 px-3 py-2">
                <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                    {a.label}
                    {(a.is_new || a.worsened) && <Badge variant="outline" className="text-[10px] border-amber-400 text-amber-700">NEW</Badge>}
                    {a.empirical_trend === 'down' && <span className="inline-flex items-center gap-0.5 text-[10px] text-rose-600"><TrendingDown className="h-3 w-3" />sliding</span>}
                  </p>
                  <p className="text-[11px] text-slate-500">Designed influence <b>{a.influence}%</b> · observed correlation only <b>{a.empirical_r}%</b> — worth checking the learning is transferring.</p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
